import * as React from 'react'
import clsx from 'clsx'
import { GridVirtualScroller } from './virtualization/GridVirtualScroller'
import { GridVirtualScrollerContent } from './virtualization/GridVirtualScrollerContent'
import { GridVirtualScrollerRenderZone } from './virtualization/GridVirtualScrollerRenderZone'
import { useGridVirtualScroller } from '../hooks/features/virtualization/useGridVirtualScroller'
import { GridOverlays } from '../components/base/GridOverlays'
import type { RenderContext } from '../models/params/gridScrollParams'
import style from '../style'
import { useGridRootProps } from '../hooks/utils/useGridRootProps'
import { GridRow, GridRowProps } from '../components/GridRow'
import { GridSkeletonRow } from '../components/GridSkeletonRow'
import { GridFooterRow } from '../components/GridFooterRow'
import { useGridApiContext } from '../hooks/utils/useGridApiContext'
import { GridReactTableInstance } from '../models/gridReactTable'
import { GridVirtualScrollerPinnedRows } from '../components/virtualization/GridVirtualScrollerPinnedRows'

interface DataGridVirtualScrollerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  disableVirtualization?: boolean
}

export const DataGridVirtualScroller = React.forwardRef<
  HTMLDivElement,
  DataGridVirtualScrollerProps
>(function DataGridVirtualScroller(props, ref) {
  const { disableVirtualization, ...other } = props
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const leftColumns = React.useRef<HTMLDivElement>(null)
  const rightColumns = React.useRef<HTMLDivElement>(null)
  const topPinnedRowsRenderZoneRef = React.useRef<HTMLDivElement>(null)
  const bottomPinnedRowsRenderZoneRef = React.useRef<HTMLDivElement>(null)
  const handleRenderZonePositioning = React.useCallback(
    ({ top, left }: { top: number; left: number }) => {
      if (leftColumns.current) {
        leftColumns.current.style.transform = `translate3d(0px, ${top}px, 0px)`
      }
      if (rightColumns.current) {
        rightColumns.current.style.transform = `translate3d(0px, ${top}px, 0px)`
      }
      if (topPinnedRowsRenderZoneRef.current) {
        topPinnedRowsRenderZoneRef.current!.style.transform = `translate3d(${left}px, 0px, 0px)`
      }
      if (bottomPinnedRowsRenderZoneRef.current) {
        bottomPinnedRowsRenderZoneRef.current!.style.transform = `translate3d(${left}px, 0px, 0px)`
      }
    },
    []
  )

  const { getRootProps, getContentProps, getRenderZoneProps, renderContext } =
    useGridVirtualScroller({
      ref,
      disableVirtualization,
      onRenderZonePositioning: handleRenderZonePositioning,
    })

  const contentProps = getContentProps()

  return (
    <GridVirtualScroller {...getRootProps(other)}>
      <GridOverlays />
      {rootProps.footerPlacement === 'top' && (
        <GridVirtualScrollerPinnedRows
          position="top"
          role="rowgroup"
          style={{
            height: renderContext.columnFooterHeight,
            width: contentProps.style.width,
          }}
        >
          {apiRef.current.tableInstance.getLeftVisibleLeafColumns().length ? (
            <div
              className={clsx(
                style.pinnedColumns,
                rootProps.classes?.pinnedColumns,
                style['pinnedColumns--left'],
                rootProps.classes?.['pinnedColumns--left']
              )}
            >
              {renderPinnedLeftRightFooter(
                renderContext,
                apiRef.current.tableInstance,
                'left'
              )}
              {/*left-top-pinned-rows */}
            </div>
          ) : null}
          <div
            className={rootProps.classes?.pinnedRowsRenderZone}
            style={{ position: 'absolute' }}
            ref={topPinnedRowsRenderZoneRef}
            role="presentation"
          >
            {renderFooterVirtual(renderContext, apiRef.current.tableInstance)}
            {/*scrollable-pinned-rows */}
          </div>
          {apiRef.current.tableInstance.getRightVisibleLeafColumns().length ? (
            <div
              className={clsx(
                style.pinnedColumns,
                rootProps.classes?.pinnedColumns,
                style['pinnedColumns--right'],
                rootProps.classes?.['pinnedColumns--right']
              )}
            >
              {renderPinnedLeftRightFooter(
                renderContext,
                apiRef.current.tableInstance,
                'right'
              )}
            </div>
          ) : null}
        </GridVirtualScrollerPinnedRows>
      )}

      <GridVirtualScrollerContent {...getContentProps()}>
        <div
          ref={leftColumns}
          className={clsx(
            style.pinnedColumns,
            rootProps.classes?.pinnedColumns,
            style['pinnedColumns--left'],
            rootProps.classes?.['pinnedColumns--left']
          )}
        >
          {apiRef.current.tableInstance.getLeftVisibleLeafColumns().length
            ? renderPinnedLeftRight(
                renderContext,
                apiRef.current.tableInstance,
                'left'
              )
            : null}
        </div>
        <GridVirtualScrollerRenderZone {...getRenderZoneProps()}>
          {renderCenter(renderContext, apiRef.current.tableInstance)}
        </GridVirtualScrollerRenderZone>
        <div
          ref={rightColumns}
          className={clsx(
            style.pinnedColumns,
            rootProps.classes?.pinnedColumns,
            style['pinnedColumns--right'],
            rootProps.classes?.['pinnedColumns--right']
          )}
        >
          {apiRef.current.tableInstance.getRightVisibleLeafColumns().length
            ? renderPinnedLeftRight(
                renderContext,
                apiRef.current.tableInstance,
                'right'
              )
            : null}
        </div>
      </GridVirtualScrollerContent>
      {rootProps.footerPlacement === 'bottom' && (
        <GridVirtualScrollerPinnedRows
          position="bottom"
          role="rowgroup"
          style={{
            height: renderContext.columnFooterHeight,
            width: contentProps.style.width,
          }}
        >
          {apiRef.current.tableInstance.getLeftVisibleLeafColumns().length ? (
            <div
              className={clsx(
                style.pinnedColumns,
                rootProps.classes?.pinnedColumns,
                style['pinnedColumns--left'],
                rootProps.classes?.['pinnedColumns--left']
              )}
            >
              {renderPinnedLeftRightFooter(
                renderContext,
                apiRef.current.tableInstance,
                'left'
              )}
            </div>
          ) : null}
          <div
            className={rootProps.classes?.pinnedRowsRenderZone}
            style={{ position: 'absolute' }}
            ref={bottomPinnedRowsRenderZoneRef}
            role="presentation"
          >
            {renderFooterVirtual(renderContext, apiRef.current.tableInstance)}
          </div>
          {apiRef.current.tableInstance.getRightVisibleLeafColumns().length ? (
            <div
              className={clsx(
                style.pinnedColumns,
                rootProps.classes?.pinnedColumns,
                style['pinnedColumns--right'],
                rootProps.classes?.['pinnedColumns--right']
              )}
            >
              {renderPinnedLeftRightFooter(
                renderContext,
                apiRef.current.tableInstance,
                'right'
              )}
            </div>
          ) : null}
        </GridVirtualScrollerPinnedRows>
      )}
    </GridVirtualScroller>
  )
})

const renderCenter = (
  renderContext: RenderContext,
  tableInstance: GridReactTableInstance
) => {
  const rows = renderContext.rowRanges.map((current, _index) => {
    const row = renderContext.rows[current.index]
    if (row) {
      const focusedCell =
        renderContext.cellFocus !== null &&
        renderContext.cellFocus.rowId === row?.id
          ? renderContext?.cellFocus.field
          : null
      let tabbableCell: GridRowProps['tabbableCell'] = null
      if (
        renderContext.cellTabIndex !== null &&
        renderContext.cellTabIndex.rowId === row?.id
      ) {
        tabbableCell = renderContext.cellTabIndex.field
      }
      return (
        // @ts-ignore
        <GridRow
          key={row?.id ?? _index}
          style={{ height: renderContext.rowHeight }}
          rowHeight={renderContext.rowHeight}
          containerWidth={renderContext.containerWidth}
          side="center"
          colRange={renderContext.colRanges}
          focusedCell={focusedCell}
          tabbableCell={tabbableCell}
          {...row}
        />
      )
    } else {
      return (
        <GridSkeletonRow
          key={`skeleton-${_index}`}
          index={_index}
          rowHeight={renderContext.rowHeight}
          containerWidth={renderContext.containerWidth}
          side="center"
          colRange={renderContext.colRanges}
          {...tableInstance}
        />
      )
    }
  })

  return rows
}

const renderPinnedLeftRight = (
  renderContext: RenderContext,
  tableInstance: GridReactTableInstance,
  side: 'left' | 'right'
) => {
  const rows = renderContext.rowRanges.map((current, _index) => {
    const row = renderContext.rows[current.index]
    if (row) {
      const focusedCell =
        renderContext.cellFocus !== null &&
        renderContext.cellFocus.rowId === row?.id
          ? renderContext?.cellFocus.field
          : null
      let tabbableCell: GridRowProps['tabbableCell'] = null
      if (
        renderContext.cellTabIndex !== null &&
        renderContext.cellTabIndex.rowId === row?.id
      ) {
        tabbableCell = renderContext.cellTabIndex.field
      }
      return (
        // @ts-ignore
        <GridRow
          key={row?.id}
          style={{ height: renderContext.rowHeight }}
          rowHeight={renderContext.rowHeight}
          containerWidth={renderContext.containerWidth}
          side={side}
          colRange={[]}
          focusedCell={focusedCell}
          tabbableCell={tabbableCell}
          {...row}
        />
      )
    } else {
      return (
        <GridSkeletonRow
          key={`skeleton-${_index}`}
          index={_index}
          rowHeight={renderContext.rowHeight}
          containerWidth={renderContext.containerWidth}
          side={side}
          colRange={renderContext.colRanges}
          {...tableInstance}
        />
      )
    }
  })

  return rows
}

const renderFooterVirtual = (
  renderContext: RenderContext,
  tableInstance: GridReactTableInstance
) => {
  return (
    <GridFooterRow
      key={`footer-row`}
      index={-1}
      rowHeight={renderContext.columnFooterHeight}
      containerWidth={renderContext.containerWidth}
      side="center"
      colRange={renderContext.colRanges}
      {...tableInstance}
    />
  )
}

const renderPinnedLeftRightFooter = (
  renderContext: RenderContext,
  tableInstance: GridReactTableInstance,
  side: 'left' | 'right'
) => {
  return (
    <GridFooterRow
      key={`footer-row-${side}`}
      index={-1}
      rowHeight={renderContext.columnFooterHeight}
      containerWidth={renderContext.containerWidth}
      side={side}
      colRange={renderContext.colRanges}
      {...tableInstance}
    />
  )
}
